<template>
  <div class="add-service-modal-body">
    <v-toolbar class="modal-toolbar" density="comfortable" flat>
      <div class="modal-bar">
        <v-btn icon variant="text" class="back-btn" aria-label="Back" @click="modal.close()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="modal-title">Add service</div>
      </div>
    </v-toolbar>

    <v-card-text class="body py-6">
      
      <v-form class="form" @submit.prevent="handleSubmit">
        <AppTextField
          v-model="title"
          label="Service name"
          placeholder="e.g., Ant control"
          :error-messages="titleError"
          @update:modelValue="titleError = ''"
        />

        <AppTextArea
          v-model="subtitle"
          label="Description"
          placeholder="Short description..."
          rows="4"
          :error-messages="subtitleError"
          @update:modelValue="subtitleError = ''"
        />

        <AppTextField
          v-model="price"
          type="number"
          label="Price"
          placeholder="0.00"
          :error-messages="priceError"
          @update:modelValue="priceError = ''"
        />

        <div class="actions">
        
          <AppButtonFlat block type="submit" prepend-icon="mdi-check" color="primary">
            Submit
          </AppButtonFlat>
        </div>
      </v-form>
    </v-card-text>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
import { useModalStore } from "@/shared/stores/modal";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";

type ServiceOption = {
  service_name: string;
  subtitle: string;
  price?: string | number | null;
};

const props = defineProps<{
  onAdd?: (service: ServiceOption) => void;
}>();

const modal = useModalStore();

const title = ref("");
const subtitle = ref("");
const price = ref<string | number | "">("");

// Error messages for validation
const titleError = ref("");
const subtitleError = ref("");
const priceError = ref("");

function handleSubmit() {
  // Clear previous errors
  titleError.value = "";
  subtitleError.value = "";
  priceError.value = "";

  const t = title.value.trim();
  if (!t) {
    titleError.value = "Service name is required";
    return;
  }

  const trimmedSubtitle = subtitle.value.trim();
  if (trimmedSubtitle.length > 250) {
    subtitleError.value = "Description must be 250 characters or less";
    return;
  }

  const servicePrice = price.value === "" ? null : price.value;
  const numericPrice = servicePrice === null ? null : Number(servicePrice);
  if (servicePrice === null || Number.isNaN(numericPrice) || numericPrice <= 0) {
    priceError.value = "Service price is required and must be greater than 0";
    return;
  }

  const service: ServiceOption = {
    service_name: t,
    subtitle: trimmedSubtitle,
    price: numericPrice,
  };

  props.onAdd?.(service);
  modal.close();
}
</script>

<style scoped>
.add-service-modal-body {
  padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 16px);
  min-height: 100dvh;
}

.modal-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-theme);
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 0 0;
  height: auto;
}

.modal-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
}

:deep(.modal-toolbar .v-toolbar__content) {
  height: auto;
  min-height: 64px;
  align-items: center;
  padding: 8px 16px;
}

.back-btn {
  width: 50px;
  height: 50px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.body {
  padding-top: 12px;
}

.form {
  display: grid;
  gap: 12px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
}
</style>
